<template>

	<Head title="Welcome" />

	<v-row class="justify-center">
		<v-col cols="12" class="pa-0">
			<div class="min-h-[96vh] align-content-center welcome-header">
				<span>
					<h1
						class="text-center text-6xl font-bold drop-shadow-[1px_1px_2px_black] text-transparent bg-clip-text bg-gradient-to-t from-orange-800/50 via-amber-100/80 to-transparent">
						🔒 unhash
					</h1>
					<h1
						class="text-center text-6xl font-bold drop-shadow-[1px_1px_2px_black] text-transparent bg-clip-text bg-gradient-to-t from-orange-800/50 via-transparent to-transparent reflection">
						🔒 unhash
					</h1>
				</span>
				<p class="text-center text-h6 text-white mt-2">Resolve phone numbers from SHA-256 hashes</p>
				<div class="d-flex justify-center mt-4" style="gap: 12px;">
					<template v-if="$user && $user.id">
						<v-btn color="primary" size="large" prepend-icon="mdi-view-dashboard-outline" href="/dashboard"
							@click.prevent.stop="goTo('/dashboard')">
							Go to Dashboard
						</v-btn>
					</template>
					<template v-else>
						<v-btn v-if="canLogin" color="primary" size="large" href="/login" @click.prevent.stop="goTo('/login')">
							Login
						</v-btn>
						<v-btn v-if="canRegister" variant="outlined" size="large" href="/register"
							@click.prevent.stop="goTo('/register')">
							Register
						</v-btn>
					</template>
				</div>
				<div class="backdrop-blur backdrop-opacity-70 bg-white-700/60 shadow-2xl absolute bottom-0 w-[100%]">
					<p class="text-center typing-text text-ellipsis text-nowrap skew-x-12">
						<span id="typed" class="font-mono"></span>
					</p>
				</div>
			</div>

			<v-container class="py-12 pb-0">
				<v-row justify="center">
					<v-col cols="12" md="9" lg="8">
						<hash-check-form :rate-limit="homepageLookupRateLimit"></hash-check-form>
					</v-col>
				</v-row>
			</v-container>

			<v-container class="py-12">
				<v-row justify="center">
					<v-col cols="12" class="text-center mb-4">
						<h2 class="text-h4 font-weight-bold">How it works</h2>
					</v-col>

					<v-col cols="12" md="4">
						<v-card variant="tonal" class="pa-2 h-100">
							<v-card-text class="text-center">
								<v-icon size="40" color="primary" class="mb-2">mdi-magnify</v-icon>
								<h3 class="text-h6 mb-1">Single Lookup</h3>
								<p class="text-medium-emphasis">
									POST a SHA-256 hash to <code>/api/v1/lookup</code> and get the matching phone number back instantly.
								</p>
							</v-card-text>
						</v-card>
					</v-col>

					<v-col cols="12" md="4">
						<v-card variant="tonal" class="pa-2 h-100">
							<v-card-text class="text-center">
								<v-icon size="40" color="primary" class="mb-2">mdi-format-list-bulleted</v-icon>
								<h3 class="text-h6 mb-1">Batch Lookup</h3>
								<p class="text-medium-emphasis">
									Resolve up to {{ batchMax }} hashes in a single call via <code>/api/v1/lookup/batch</code>.
								</p>
							</v-card-text>
						</v-card>
					</v-col>

					<v-col cols="12" md="4">
						<v-card variant="tonal" class="pa-2 h-100">
							<v-card-text class="text-center">
								<v-icon size="40" color="primary" class="mb-2">mdi-key-variant</v-icon>
								<h3 class="text-h6 mb-1">Simple API Keys</h3>
								<p class="text-medium-emphasis">
									Authenticate with a personal Sanctum API token, managed right from your dashboard.
									Rate limited to {{ rateLimit }} requests/min per key.
								</p>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
			</v-container>

			<v-container class="py-0 pb-12">
				<v-row justify="center">
					<v-col cols="12" class="text-center mb-4">
						<h2 class="text-h4 font-weight-bold">Quick start</h2>
						<p class="text-medium-emphasis">Swap in your own API token from <code>/user/api-tokens</code> and go.</p>
					</v-col>
					<v-col cols="12" md="9" lg="8">
						<api-usage :batch-max="batchMax"></api-usage>
					</v-col>
				</v-row>
			</v-container>

			<v-menu open-on-hover :close-delay="2500" absolute top transition="scale-transition" origin="bottom left">
				<template v-slot:activator="{ props }">
					<v-chip class="designer lime darken-1" v-bind="props" size="x-large">
						<v-card>
							<v-card-text class="">
								<p class="my-0 black--text">Designed and Maintained By:</p>
								<p class="my-0">
									<v-icon>mdi-whatsapp</v-icon>
									<a href="https://wa.me/MKinyua53" class="pull-right black--text">@MKinyua53</a>
								</p>
							</v-card-text>
						</v-card>
					</v-chip>
				</template>
				<v-list two-line dense>
					<v-list-item href="https://wa.me/MKinyua53" target="_blank" prepend-icon="mdi-whatsapp" title="WhatsApp"
						subtitle="MKinyua53" />
					<v-list-item href="https://x.com/MKinyua53" target="_blank" prepend-icon="mdi-twitter" title="Twitter"
						subtitle="@MKinyua53" />
					<v-list-item href="https://linkedin.com/in/mwathakinyua" target="_blank" prepend-icon="mdi-linkedin"
						title="LinkedIn" subtitle="Mwatha Kinyua" />
					<v-list-item href="https://gitlab.com/MKinyua53" target="_blank" prepend-icon="mdi-gitlab" title="Gitlab"
						subtitle="MKinyua53" />
				</v-list>
			</v-menu>
		</v-col>
	</v-row>

</template>

<script setup>
	import AppLayout from '@/Layouts/Guest.vue'
	import { onMounted, ref } from 'vue'
	import Typed from 'typed.js'
	import ApiUsage from '@/Components/Home/ApiUsage.vue'
	import HashCheckForm from '@/Components/Home/HashCheckForm.vue'

	defineOptions({
		layout: AppLayout,
		name: 'Title',
	})

	const props = defineProps({
		canLogin: Boolean,
		canRegister: Boolean,
		laravelVersion: String,
		phpVersion: String,
		rateLimit: Number,
		batchMax: Number,
		homepageLookupRateLimit: Number,
	})

	const introText = ref([
		'Single Number Lookup: POST /api/v1/lookup',
		'Batch Lookup: POST /api/v1/lookup/batch',
		'SHA-256 → phone number',
	])

	function mounted() {
		new Typed('#typed', {
			strings: introText.value,
			typeSpeed: 50,
			shuffle: false,
			loop: true,
			startDelay: 200,
			smartBackspace: false,
			// cursorChar: '_',
			backDelay: 2000,
			backSpeed: 10,
		})
	}

	onMounted(() => {
		mounted()
	})

</script>

<style scoped>
	.welcome-header {
		position: relative;
		background-image: linear-gradient(to top right, rgba(255, 0, 0, 0.225), rgba(0, 45, 128, 0.716)), url('https://picsum.photos/2000/800');
		background-position: center center;
		background-repeat: no-repeat;
		background-size: cover;
		background-attachment: fixed;
	}

	.typing-text {
		font-size: 1.5rem;
		background-image: linear-gradient(rgba(181, 179, 179, 0.206), rgba(186, 198, 50, 0.532)), url('https://picsum.photos/2000/300/?blur');
		background-position: center center;
		background-repeat: no-repeat;
		background-size: cover;
		background-attachment: fixed;
		background-clip: text;
		color: transparent;
	}

	.reflection {
		transform: rotatex(180deg) translatey(6px);
	}

	.designer {
		position: fixed;
		left: 1px;
		bottom: 72px;
		width: fit-content;
	}

</style>
